import $http from '@/utils/axios';
import type { AxiosInstance } from 'axios';
import type { ApiResponse, PageResult, SortOptions } from '@/types/common';

export class CrudService<T = any> {
  protected $http: AxiosInstance;
  public resource: string;

  constructor(resource: string) {
    this.$http = $http;
    this.resource = resource;
  }

  protected _encodeQuery(query: Record<string, any>): string {
    return btoa(encodeURIComponent(JSON.stringify(query)));
  }

  insert(data: Partial<T>): Promise<ApiResponse<T>> {
    return this.$http.post(`/${this.resource}`, data);
  }

  getById(id: string | number): Promise<ApiResponse<T>> {
    return this.$http.get(`/${this.resource}/${id}`);
  }

  updateById(id: string | number, data: Partial<T>): Promise<ApiResponse<T>> {
    return this.$http.put(`/${this.resource}/${id}`, data);
  }

  batchUpdate(arr: Partial<T>[] = []): Promise<ApiResponse<any>> {
    return this.$http.put(`/${this.resource}/batchUpdate`, arr);
  }

  deleteById(id: string | number): Promise<ApiResponse<any>> {
    return this.$http.delete(`/${this.resource}/${id}`);
  }

  deleteByIds(ids: (string | number)[]): Promise<ApiResponse<any>> {
    return this.$http.delete(`/${this.resource}/deleteByIds`, {
      data: ids
    });
  }

  deleteByQuery(query: Record<string, any>): Promise<ApiResponse<any>> {
    return this.$http.delete(`/${this.resource}/deleteByQuery`, {
      params: {
        query: this._encodeQuery(query)
      }
    });
  }

  list(query: Record<string, any> = {}, sortOptions: SortOptions = {}): Promise<ApiResponse<T[]>> {
    const params = {
      query: btoa(encodeURIComponent(JSON.stringify(query))),
      sort: btoa(JSON.stringify(sortOptions))
    };

    return this.$http.get(`/${this.resource}/list`, {
      params
    });
  }

  page(page = 1, pageSize = 10, query: Record<string, any> = {}, sortOptions: SortOptions = {}): Promise<ApiResponse<PageResult<T>>> {
    const sort: Record<string, any> = {};
    if (Object.keys(sortOptions).length === 0) {
      sort.createdTime = -1;
    }
    Object.keys(sortOptions).forEach(key => {
      const value = sortOptions[key];
      if (typeof value === 'number') {
        sort[key] = value;
        return;
      }
      sort[key] = value === 'asc' ? 1 : -1;
    });

    const params = {
      page,
      pageSize,
      query: this._encodeQuery(query),
      sort: btoa(JSON.stringify(sort))
    };

    return this.$http.get(`/${this.resource}/page`, {
      params
    });
  }
}

export default CrudService;
